import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Pencil } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Logo from "@/components/Logo";
import RacePlanBody from "@/components/race/RacePlanBody";
import { raceNumbers, fmtMin } from "@/components/race/RaceBlocks";
import { toast } from "sonner";

// One-off Race Pack buyers see exactly the same race plan a full subscriber
// gets — the body is the shared RacePlanBody component.
export default function RacePackPlan() {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [startTime, setStartTime] = useState("08:00");
  const [raceMins, setRaceMins] = useState("");

  useEffect(() => {
    api.get(`/race-pack/plans/${planId}`)
      .then(({ data }) => {
        setPlan(data);
        const storedStart = localStorage.getItem(`spc-race-start-${planId}`);
        setStartTime(storedStart || data?.raceWeek?.startTime || "08:00");
        const stored = localStorage.getItem(`spc-race-mins-${planId}`);
        const dur = ((((data?.generated?.fuelling) || [])[data?.raceWeek?.dayIndex] || {}).sessions || [])[0]?.duration;
        setRaceMins(stored || (dur ? String(dur) : ""));
      })
      .catch((e) => {
        setError(apiError(e, "Could not open that race plan."));
        toast.error(apiError(e, "Could not open that race plan."));
      });
  }, [planId]);

  const saveMins = (v) => {
    setRaceMins(v);
    try { localStorage.setItem(`spc-race-mins-${planId}`, v); } catch { /* quota */ }
  };
  const saveStart = (v) => {
    setStartTime(v);
    try { localStorage.setItem(`spc-race-start-${planId}`, v); } catch { /* quota */ }
  };

  const daily = plan?.generated?.targets?.daily || [];
  const fuelling = plan?.generated?.fuelling || [];
  const raceIdx = plan?.raceWeek?.dayIndex;
  const raceSession = ((fuelling[raceIdx] || {}).sessions || [])[0];
  const weightKg = plan?.profile?.weight || 0;

  const n = useMemo(() => raceNumbers({
    durMin: Number(raceMins) || raceSession?.duration || 0,
    weightKg,
    carbsPerHour: plan?.raceWeek?.carbsPerHour,
    heat: !!plan?.raceWeek?.heat,
    fuelling: plan?.raceWeek?.fuelling || {},
    sport: raceSession?.sport || "",
  }), [raceMins, raceSession, weightKg, plan]);

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white grid place-items-center p-6">
        <div className="text-center max-w-sm" data-testid="race-plan-error">
          <p className="text-sm text-zinc-300">{error}</p>
          <button onClick={() => navigate("/race-pack")}
            className="mt-6 bg-[#E2E30A] text-black font-display font-black uppercase tracking-wider px-5 py-3">
            Back to my race packs
          </button>
        </div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#E2E30A]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white pb-20 race-print-root" data-testid="race-plan-page">
      <header className="border-b border-white/10 no-print">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/race-pack" className="flex items-center gap-2.5">
            <Logo className="h-9 w-9" />
            <span className="font-display text-base uppercase tracking-tight">
              <span className="text-[#E2E30A]">Race</span> Plan
            </span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to={`/race-pack/${planId}/edit`} data-testid="race-plan-edit"
              className="inline-flex items-center gap-1.5 border border-[#E2E30A]/60 text-[#E2E30A] hover:bg-[#E2E30A] hover:text-black px-3 py-2 text-[10px] font-bold uppercase tracking-widest transition-colors">
              <Pencil className="h-3.5 w-3.5" /> Edit plan
            </Link>
            <Link to="/race-pack" data-testid="race-plan-back"
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-400 hover:text-white">
              <ArrowLeft className="h-3.5 w-3.5" /> My packs
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#E2E30A] font-bold">
            Race fuelling plan · {fmtMin(n.durMin)} · {plan.raceWeek?.heat ? "hot conditions" : "mild conditions"}
            {plan.coachBuilt ? " · built by your coach" : ""}
          </p>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tighter font-black mt-2">{plan.name}</h1>
        </div>

        <RacePlanBody
          name={plan.name} raceIso={plan.raceDate} planId={planId}
          startTime={startTime} onStartTime={saveStart}
          raceMins={raceMins} onRaceMins={saveMins}
          n={n} weightKg={weightKg} daily={daily} fuelling={fuelling} raceIdx={raceIdx}
          strategy={plan.raceWeek?.strategy} sport={raceSession?.sport}
        />

        <p className="text-xs text-zinc-500 no-print">
          This race plan stays open until 7 days after race day.{" "}
          <Link to="/subscribe" className="text-[#E2E30A] underline hover:no-underline">Go full plan</Link>{" "}
          for weekly meal plans, the food tracker and every race after this one.
        </p>
      </main>
    </div>
  );
}
